import * as React from 'react';
import * as spaUrls from 'utils/spaUrls';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Checkbox, CircularProgress, Link, Typography } from '@mui/material';
import usePatientProviderMap from 'pages/ScalesManifest/hooks/usePatientProviderMap';
import Filters, { TFilters } from 'pages/ScalesManifest/components/Filters';
import usePDFExport from 'pages/ScalesManifest/hooks/usePDFExport';
import { TableData } from 'pages/ScalesManifest/types/scales-manifest.types';
import GetAppIcon from '@mui/icons-material/GetApp';
import useScales from 'pages/ScalesManifest/hooks/useScales';
import { format } from 'date-fns';
import PatientScaleDetailsDialog, { Patient } from 'pages/ScalesManifest/PatientScaleDetailsDialog';
import LoadingButton from 'components/Common/LoadingButton';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import { useHttp } from 'hooks/use-fetch';
import NameText from 'pages/Dashboard/components/NameText';
import { useQuery } from 'react-query';
import { patientUrls, providers } from 'utils/apiUrls';
import { fetchProvidersAssociationsList, fetchUserMetadata } from 'pages/Dashboard/services/provider.services';
import { UserMetadataFlags } from 'pages/Dashboard/types';
import { PracticeProvider } from 'pages/Dashboard/types/providers.types';
import { useUnifiedAuth } from 'components/Common/UnifiedAuthContext';
import { fetchPatientList } from 'pages/Dashboard/services/patient.services';
import { Option } from 'pages/ScalesManifest/components/Autocomplete';
import { GoogleDate, googleDateToDateString } from 'utils/dateUtils';

interface TableColumn {
  id: 'providerName' | 'patientName' | 'scale' | 'score' | 'takenOn' | 'attemptId';
  label: string;
  minWidth?: number;
}

const tableColumns: readonly TableColumn[] = [
  { id: 'providerName', label: 'Provider name', minWidth: 100 },
  {
    id: 'patientName',
    label: 'Patient name',
    minWidth: 100,
  },
  {
    id: 'scale',
    label: 'Scale',
    minWidth: 100,
  },
  {
    id: 'score',
    label: 'Score',
    minWidth: 100,
  },
  {
    id: 'takenOn',
    label: 'Taken on',
    minWidth: 100,
  },
  {
    id: 'attemptId',
    label: 'Answers',
    minWidth: 100,
  },
];

export default function ScalesManifest() {
  const { providerId, http } = useHttp();
  const { user } = useUnifiedAuth();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [selectedRows, setSelectedRows] = React.useState({} as { [key: string]: TableData });
  const [selectedRow, setSelectedRow] = React.useState({} as TableData);
  const [filters, setFilters] = React.useState({} as TFilters);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [patientNameOptions, setPatientNameOptions] = React.useState<Option[]>([]);
  const [enableFetching, setEnableFetching] = React.useState(true);

  const {
    scaleNames,
    isFetching: isFetchingProviderPatientMap,
  } = usePatientProviderMap('', filters?.providerName?.value);

  const { isFetching: isPreferenceFetching } = useQuery(providers.metadata.queryUrl, {
    queryFn: fetchUserMetadata(http.post, {
      name: UserMetadataFlags.ShowAllScalesForBillingAdmin,
      userId: providerId,
    }),
    onSuccess: (data) => {
      if (data.flag) {
        const value = data.flag?.value;
        setFilters((prev) => ({
          ...prev,
          showLatestAttempts: value !== 'true',
        }));
      }
    },
    onError: () => {
      setFilters((prev) => ({
        ...prev,
        showLatestAttempts: true,
      }));
    },
    retry: false,
  });

  const providerAssociations = useQuery(
    [`${providers.providerAssociationList.queryUrl}`, providerId, 'scalesManifest'],
    {
      queryFn: fetchProvidersAssociationsList(http.get),
    },
  );

  const providersList = React.useMemo(() => {
    if (
      providerAssociations.data?.providers?.length
      && providerAssociations.data.providers.length > 0
    ) {
      return [
        {
          id: providerId,
          firstName: `${user?.name}`,
          lastName: '',
        } as PracticeProvider,
        ...providerAssociations.data.providers,
      ];
    }
    else {
      return [];
    }
  }, [providerAssociations, providerId, user?.name]);

  const {
    totalCount,
    scales,
    isFetching: isFetchingScales,
    isError,
  } = useScales({
    categoryId: filters.scale?.value || '',
    providerId: filters.providerName?.value || '',
    patientId: filters.patientName?.value || '',
    startDate: filters?.startDate,
    endData: filters?.endDate,
    attemptId: '',
    page,
    size: rowsPerPage,
    enabled: enableFetching && !isPreferenceFetching,
    billingAdminId: providerId,
    showLatestAttempts: filters?.showLatestAttempts,
  });

  const tableRows = React.useMemo(() => scales, [scales]);

  const providerNameOptions = React.useMemo(
    () => Object.values(providersList)
      .sort((a, b) => a.lastName.localeCompare(b.lastName))
      .map((provider) => ({
        label: `${provider.firstName} ${provider.lastName}`,
        value: provider.id,
      })),
    [providersList],
  );

  const scaleOptions = React.useMemo(
    () => Object.keys(scaleNames).map((id) => ({
      label: scaleNames[id],
      value: id,
    })),
    [scaleNames],
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const { isFetching: isFetchingPatientsList, isError: isErrorPatientsList } = useQuery(
    [patientUrls.listPatients.queryUrl, providerId, undefined, searchQuery, undefined, undefined,
      undefined, undefined, filters.providerName?.value],
    {
      queryFn: fetchPatientList(http.get, true),
      enabled: !!providerId && !!searchQuery,
      refetchOnMount: 'always',
      onSuccess({ patients }) {
        const options = patients.map((patient) => ({
          label: `${patient?.name?.firstName} ${patient?.name?.lastName}`,
          value: patient?.id as string,
        }));
        setPatientNameOptions(options);
      },
    },
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    if (!event.target.value) {
      setPatientNameOptions([]);
    }
  };

  const goToAnswers = React.useCallback((row: TableData) => {
    setSelectedRow(row);
    trackMixpanelEvent(MixpanelEventName.REPORTS_SEE_ANSWERS_BUTTON_CLICKED, {
      scale: row.attemptId,
      'patient id': row.patientId,
      'provider id': row.providerId,
    });
  }, []);

  const rows: TableData[] = React.useMemo(() => {
    const { patientName, providerName, scale } = filters;
    const updatedRows = [...tableRows];
    const filteredRows = updatedRows.reduce((acc: TableData[], row: TableData) => {
      const isPatientNameMatch = !patientName?.value || patientName?.value === row.patientId;
      const isProviderNameMatch = !providerName?.value || providerName?.value === row.providerId;
      const isScaleMatch = !scale?.value || scale?.value === row.categoryId;
      const provider = providersList.find(
        (p) => p.id === row.providerId,
      );
      const fullProviderName = provider
        ? `${provider.firstName} ${provider.lastName}`
        : '';
      if (
        isPatientNameMatch
          && isProviderNameMatch
          && isScaleMatch
          && row.patient?.name?.firstName
      ) {
        acc.push({
          ...row,
          patientName: `${row.patient?.name?.firstName} ${row.patient?.name?.lastName}` as string,
          patientDOB: googleDateToDateString(row.patient?.dateOfBirth as GoogleDate) as string,
          providerName: fullProviderName,
          scale: row.categoryAbbreviation,
          takenOn: format(new Date(row.attemptedAt), 'M/d/yyyy'),
          score: `${row.attemptResult.score}/${row.attemptResult.totalScore}`,
        });
      }

      return acc;
    }, []);
    return filteredRows;
  }, [providersList, filters, tableRows]);

  const targetRef = React.useRef<HTMLDivElement>(null);

  const selectedRowsData = React.useMemo(() => Object.values(selectedRows), [selectedRows]);

  const renderComponent = React.useCallback(
    (ref, details, questions) => (
      <Patient questions={questions} targetRef={ref} details={details} />
    ),
    [],
  );

  const { isExecuting, executePDFExport } = usePDFExport({
    exportingRows: selectedRowsData,
    renderComponent,
    afterExecution: () => {
      trackMixpanelEvent(MixpanelEventName.REPORTS_DOWNLOAD_PDF_BUTTON_CLICKED, {
        scales: selectedRowsData.map((row) => row.attemptId).join(', '),
        location: 'Reports table',
      });
      setSelectedRows({});
    },
  });

  const onSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const selectedRowsMap = rows.reduce((acc: { [key: string]: TableData }, row: TableData) => {
        acc[row.attemptId] = row;
        return acc;
      }, {});
      setSelectedRows({
        ...selectedRowsMap,
      });
    }
    else {
      setSelectedRows({});
    }
  };

  const onSelectRow = (e: React.ChangeEvent<HTMLInputElement>, row: TableData) => {
    setSelectedRows((prevRows) => {
      const updatedRows = { ...prevRows };
      if (e.target.checked) {
        updatedRows[row.attemptId] = row;
        return updatedRows;
      }
      else {
        delete updatedRows[row.attemptId];
        return updatedRows;
      }
    });
  };

  const numberOfSelectedRows = React.useMemo(() => selectedRowsData.length, [selectedRowsData]);

  const isFetching = React.useMemo(
    () => isFetchingProviderPatientMap || isFetchingScales,
    [isFetchingProviderPatientMap, isFetchingScales],
  );

  const renderColumn = React.useCallback(
    (column: TableColumn, row: TableData) => {
      if (column.id === 'patientName') {
        const patientName = `${row.patient?.name?.firstName} ${row.patient?.name?.lastName}` as string;
        const nameObject = {
          firstName: patientName?.split(' ')[0],
          lastName: patientName?.split(' ')[1],
        };
        return (
          <Link
            component='a'
            underline='hover'
            href={spaUrls.mainNav.patientDetail(row.patientId)}
            target='_blank'
            onClick={() => trackMixpanelEvent(MixpanelEventName.REPORTS_PATIENT_NAME_LINK_CLICKED, {
              'patient id': row.patientId,
            })}
          >
            <NameText userName={nameObject} />
          </Link>
        );
      }
      else if (column.id === 'attemptId') {
        return (
          <Link onClick={() => goToAnswers(row)} component='button' underline='hover'>
            See answers
          </Link>
        );
      }
      return row[column.id];
    },
    [goToAnswers],
  );

  const onChangeFilters = React.useCallback((callback: React.SetStateAction<TFilters>) => {
    setEnableFetching(false);
    setPage(0);
    setFilters(callback);
    setEnableFetching(true);
  }, []);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }} ref={targetRef}>
      <Typography
        variant='h4'
        gutterBottom
        justifyContent='space-between'
        display='flex'
        width='100%'
      >
        Patient Scales
      </Typography>
      <Filters
        filters={filters}
        setFilters={onChangeFilters}
        providerNameOptions={providerNameOptions}
        patientNameOptions={patientNameOptions}
        scaleOptions={scaleOptions}
        isFetching={isFetching}
        onSearchChange={handleSearchChange}
        isFetchingPatientsList={isFetchingPatientsList}
      />
      <LoadingButton
        sx={{ ml: 1.9 }}
        variant='contained'
        startIcon={<GetAppIcon />}
        onClick={executePDFExport}
        label='DOWNLOAD PDF'
        isLoading={isExecuting}
        disabled={!selectedRowsData.length}
      />
      <TableContainer
        sx={{
          minHeight: 600,
          display: 'flex',
          alignItems: isFetching ? 'center' : 'flex-start',
          justifyContent: 'center',
        }}
      >
        {isFetching && <CircularProgress data-testid='scales-loader' />}
        {(!isFetching && !isFetchingPatientsList && (isError || isErrorPatientsList)) && (
          <Typography variant='h5' mt={10} color='warning'>
            Error loading data
          </Typography>
        )}
        {!isFetching && !isFetchingPatientsList
        && !isError && !isErrorPatientsList && rows.length === 0 && (
        <Typography variant='h5' mt={10}>
          No data found for the selected filters
        </Typography>
        )}
        {!isFetching && rows.length > 0 && (
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox'>
                  <Checkbox
                    color='primary'
                    indeterminate={numberOfSelectedRows > 0 && numberOfSelectedRows < rows.length}
                    checked={numberOfSelectedRows === rows.length}
                    onChange={onSelectAll}
                    disabled={isExecuting}
                    inputProps={{
                      'aria-label': 'select all desserts',
                    }}
                  />
                </TableCell>
                {tableColumns.map((column) => (
                  <TableCell key={column.id} style={{ minWidth: column.minWidth }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: TableData) => (
                <TableRow hover role='checkbox' tabIndex={-1} key={row.attemptId + row.patientId}>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      color='primary'
                      checked={!!selectedRows[row.attemptId]?.attemptId}
                      onChange={(e) => onSelectRow(e, row)}
                      disabled={isExecuting}
                    />
                  </TableCell>
                  {tableColumns.map((column) => (
                    <TableCell key={column.id}>{renderColumn(column, row)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <PatientScaleDetailsDialog
        selectedRow={{ ...selectedRow, metaInfo: selectedRow?.attemptResult?.metaInfo }}
        setSelectedRow={setSelectedRow}
      />
    </Paper>
  );
}
